import { useMemo } from "react";

import { TertiaryButton } from "@components/v2/buttons";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";

import { type ChannelInsightsColumn } from "../model-insights-content";

import {
  EFFICIENCY_OVER_TIME,
  SPEND_VS_REVENUE,
  type ExportSelection,
} from "./export-channel-insights-modal";

interface Props {
  columns: ChannelInsightsColumn[];
  exportSelection: ExportSelection;
  setExportSelection: (value: ExportSelection) => void;
}

export const ExportChannelSelectAll = ({
  columns,
  exportSelection,
  setExportSelection,
}: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);

  const isAllSelected = useMemo(
    () =>
      Object.values(exportSelection).every((row) =>
        Object.values(row).every((value) => value),
      ),
    [exportSelection],
  );

  const isAllDeselected = useMemo(
    () =>
      Object.values(exportSelection).every((row) =>
        Object.values(row).every((value) => !value),
      ),
    [exportSelection],
  );

  const selectAll = () => {
    setExportSelection(
      columns.reduce<any>((acc, row) => {
        acc[row.column_name] = {
          [SPEND_VS_REVENUE]: true,
          [EFFICIENCY_OVER_TIME]: true,
        };
        return acc;
      }, {}),
    );
  };

  const deselectAll = () => {
    setExportSelection(
      columns.reduce<any>((acc, row) => {
        acc[row.column_name] = {
          [SPEND_VS_REVENUE]: false,
          [EFFICIENCY_OVER_TIME]: false,
        };
        return acc;
      }, {}),
    );
  };

  return (
    <div className="space-y-2 text-sm mt-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-lg">Select views to export</div>
        <div>
          <TertiaryButton
            onClick={() => {
              trackMMMEvent({
                component:
                  ModelInsightsTrackedComponent.EXPORT_CHANNEL_INSIGHTS_MODAL,
                action: "onClick",
                actionInfo: "Select all channels",
              });

              selectAll();
            }}
            disabled={isAllSelected}
          >
            SELECT ALL
          </TertiaryButton>
          <TertiaryButton
            onClick={() => {
              trackMMMEvent({
                component:
                  ModelInsightsTrackedComponent.EXPORT_CHANNEL_INSIGHTS_MODAL,
                action: "onClick",
                actionInfo: "Deselect all channels",
              });

              deselectAll();
            }}
            disabled={isAllDeselected}
          >
            DESELECT ALL
          </TertiaryButton>
        </div>
      </div>
    </div>
  );
};
